"use client"

import { Button, Spinner, Text } from "@/elements"
import { SyncUserSubscriberDocument } from "@/gql/graphql"
import { useSession } from "@/hooks/useSession"
import { ZendeskAPI } from "@/providers/ZenDesk"
import { COLORS } from "@/theme"
import { useApolloClient, useMutation } from "@apollo/client"
import { CheckoutProvider } from "@stripe/react-stripe-js"
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js"
import React, { useCallback, useState } from "react"
import { CheckoutForm } from "./CheckoutForm.tsx"

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!, {
  betas: ["custom_checkout_beta_6"],
})

const appearance: StripeElementsOptions["appearance"] = {
  theme: "stripe",
  variables: {
    colorPrimary: COLORS.fg.primary,
    colorBackground: COLORS.bg[1],
    colorText: COLORS.fg[1],
    colorTextPlaceholder: COLORS.fg[2],
    colorDanger: COLORS.red.default,
    fontFamily: "system-ui, sans-serif",
    spacingUnit: "4px",
    borderRadius: "4px",
  },
}

interface Props {
  setStep: (number: number) => void
  eligibleForFreeTrial: boolean
  selectedPlanId: string | null
  selectedProductType: "daily" | "monthly" | "yearly" | null
  setIsModalLocked: (canModalClose: boolean) => void
  setSelectedProductType: (productType: "daily" | "monthly" | "yearly" | null) => void
}

export const SubscriptionCheckoutWrapper: React.FC<Props> = ({
  setStep,
  eligibleForFreeTrial,
  selectedPlanId,
  selectedProductType,
  setIsModalLocked,
  setSelectedProductType,
}) => {
  const apolloClient = useApolloClient()
  const session = useSession()
  const [syncSubscription] = useMutation(SyncUserSubscriberDocument)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isDayPass = selectedProductType === "daily"

  const fetchClientSecretSubscription = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetch("/api/subscription/create-checkout-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          eligibleForFreeTrial,
          priceId: selectedPlanId,
          planDuration: selectedProductType,
        }),
      })
      const json = await response.json()
      if (json.error) {
        if (json.error === "User has active purchase.") {
          if (json.type === "daily") {
            setSelectedProductType(json.type)
          }
          await syncSubscription({ variables: { id: session?.data?.user.id! } })
          await apolloClient.reFetchObservableQueries()

          setStep(3)
          return ""
        }
        setError(json.error)
      }
      return `${json.success?.clientSecret}`
    } catch (error) {
      return ""
    } finally {
      setLoading(false)
    }
  }, [
    apolloClient,
    eligibleForFreeTrial,
    selectedPlanId,
    selectedProductType,
    session?.data?.user.id,
    setSelectedProductType,
    setStep,
    syncSubscription,
  ])

  const fetchClientSecretDayPass = useCallback(async () => {
    setLoading(true)
    try {
      const response = await fetch("/api/day-pass/create-checkout-single-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      })
      const json = await response.json()

      if (json.error) {
        if (json.error === "User has active purchase.") {
          if (json.type !== "daily") {
            setSelectedProductType(json.type)
          }
          await syncSubscription({ variables: { id: session?.data?.user.id! } })
          await apolloClient.reFetchObservableQueries()

          setStep(3)
          return ""
        }
        setError(json.error)
      }
      return `${json.success?.clientSecret}`
    } catch (error) {
      return ""
    } finally {
      setLoading(false)
    }
  }, [apolloClient, session?.data?.user.id, setSelectedProductType, setStep, syncSubscription])

  return (
    <>
      {loading ? (
        <div className="flex min-h-[393px] flex-col items-center justify-center">
          <Spinner />
        </div>
      ) : null}
      {error ? (
        <div className="flex flex-col items-center justify-center gap-y-2 p-4">
          <Text className="text-red-dark" variant="h6">
            Sorry, we ran into a bump while loading your checkout!{" "}
          </Text>
          <Text variant="body2">
            Please contact support with the error below:
            <br /> {error}
          </Text>
          <Button onClick={() => ZendeskAPI("messenger", "open")} className="mt-4" variant="secondary">
            Contact Support
          </Button>
        </div>
      ) : null}
      <CheckoutProvider
        stripe={stripePromise}
        options={{
          elementsOptions: {
            loader: "always",
            appearance,
          },
          fetchClientSecret: isDayPass ? fetchClientSecretDayPass : fetchClientSecretSubscription,
        }}>
        <CheckoutForm setStep={setStep} setIsModalLocked={setIsModalLocked} />
      </CheckoutProvider>
    </>
  )
}
